import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';

/** Groups exposed by GET insights/dashboards?group=<group>. */
export type InsightsDashboardGroup = 'advanced' | 'custom' | 'insights';

/** A dashboard as returned by the insights endpoint. */
export interface InsightsDashboard {
  id: string;
  name: string;
  /** Stable slug used for deep links, e.g. 'invoiceinsights'. */
  tag?: string;
  info?: string;
  description?: string;
  section?: string;
}

/** Report option shape rendered by the reports left nav (see LeftNavBar.vue). */
export interface InsightsReportOption {
  id: string;
  tag?: string;
  label: string;
  name: string;
  dateRangeOptions: boolean;
  embedded: boolean;
  canUseEditor: boolean;
  showReport: boolean;
  reportType: string;
  tooltipData: string;
  dashboardId: string;
  description?: string;
  provider: 'metabase';
  advancedReport?: boolean;
  embeddedMetabaseCustomReport?: boolean;
  isMetabaseCustomReport?: boolean;
}

const toReportOption = (
  dashboard: InsightsDashboard,
  group: InsightsDashboardGroup,
): InsightsReportOption => {
  const isAdvanced = group === 'advanced';

  return {
    id: dashboard.id,
    tag: dashboard.tag,
    label: dashboard.name,
    name: dashboard.name,
    dateRangeOptions: false,
    embedded: true,
    canUseEditor: false,
    showReport: true,
    reportType: dashboard.id,
    tooltipData:
      dashboard.info || (isAdvanced ? 'Advanced Report' : 'Custom Report'),
    dashboardId: dashboard.id,
    description: dashboard.description,
    provider: 'metabase',
    ...(isAdvanced
      ? { advancedReport: true }
      : { embeddedMetabaseCustomReport: true, isMetabaseCustomReport: true }),
  };
};

/**
 * Dashboards served by the insights endpoint (Metabase). This is the current
 * reporting infrastructure — useEmbeddedReportStore covers the older
 * embeddedreporting/Luzmo reports and is being retired.
 */
export const useInsightsDashboardStore = defineStore(
  'insightsDashboardStore',
  () => {
    const { handleError } = useSnackbarHandler();

    const dashboards = ref<Record<string, InsightsDashboard[]>>({});
    const options = ref<Record<string, InsightsReportOption[]>>({});

    /**
     * Loads one group, caching both the raw dashboards and the nav options.
     * Resolves to an empty list on failure so a group that fails to load can't
     * blank out the rest of the page.
     */
    async function list(
      group: InsightsDashboardGroup,
    ): Promise<InsightsDashboard[]> {
      try {
        const response = await axios({
          url: 'insights/dashboards',
          method: 'GET',
          params: { group },
        });
        const loaded: InsightsDashboard[] = response.data || [];

        dashboards.value = { ...dashboards.value, [group]: loaded };
        options.value = {
          ...options.value,
          [group]: loaded.map((dashboard) => toReportOption(dashboard, group)),
        };

        return loaded;
      } catch (error: unknown) {
        handleError(error);
        return [];
      }
    }

    function reportOptions(
      group: InsightsDashboardGroup,
    ): InsightsReportOption[] {
      return options.value[group] || [];
    }

    /**
     * Signed embed link for one dashboard, by tag or id. Errors are left
     * uncaught so the embedding component can show them in place instead of
     * rendering an empty frame.
     */
    async function getDashboardLink(
      tagOrId: string,
      filters?: Record<string, unknown> | null,
    ): Promise<string> {
      const response = await axios({
        url: `insights/dashboards/${tagOrId}`,
        method: 'GET',
        ...(filters ? { params: filters } : {}),
      });

      return response.data.link;
    }

    /** Left nav section for advanced reports. */
    function advancedReportGroup() {
      return [
        {
          isBeta: true,
          toggle: false,
          showReportList: true,
          isEmbeddedAdvancedReport: true,
          label: 'Advanced Reports',
          options: reportOptions('advanced'),
        },
      ];
    }

    /** Left nav section for custom reports. */
    function customReportGroup() {
      return [
        {
          isBeta: true,
          toggle: false,
          showReportList: true,
          isEmbeddedMetabaseCustomReport: true,
          label: 'Custom Reports',
          options: reportOptions('custom'),
        },
      ];
    }

    /**
     * Resolves a deep link value against the loaded advanced and custom
     * reports. Accepts either a dashboard tag (as the Overview URL uses) or an
     * id, so a link keeps working when only one of the two is at hand.
     */
    function findByTagOrId(
      tagOrId?: string | null,
    ): InsightsReportOption | null {
      if (!tagOrId) {
        return null;
      }

      const needle = String(tagOrId).toLowerCase();
      const matches = (value?: string | null) =>
        value !== undefined &&
        value !== null &&
        String(value).toLowerCase() === needle;

      return (
        [...reportOptions('advanced'), ...reportOptions('custom')].find(
          (option) => matches(option.tag) || matches(option.id),
        ) || null
      );
    }

    return {
      dashboards,
      options,
      list,
      reportOptions,
      getDashboardLink,
      advancedReportGroup,
      customReportGroup,
      findByTagOrId,
    };
  },
);
